import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './styles.module.scss';
import { EditIcon3 } from 'assets';
import { toTitleCase } from 'utils/titleCase';
import { formatYearsRange } from 'components/job-criteria';
import { useGetAssessmentInfo } from 'hooks';
import { BasicExpectationEditModal, BasicExpectationFormData, buildBasicExpectationPatch, hasBasicExpectationChanges } from 'components/edit-modals/basic-expectations';
import { CoreResponsibilitiesEditModal } from 'components/edit-modals/core-responsibilities';
import { JdQualificationsEditModal } from 'components/edit-modals/jd-qualifications';
import { useBasicExpectationsEdit } from 'hooks/criteria-modal-hooks/useBasicExpectations';
import { useCoreResponsibilitiesEdit, CoreResponsibilitiesPayload } from 'hooks/criteria-modal-hooks/useCoreResponsibilities';
import { useJdQualificationsEdit, JdQualificationsPayload } from 'hooks/criteria-modal-hooks/useJdQualifications';
import { formatWorkMode, normalizeWorkMode, shouldCollectJobLocation } from 'utils/workMode';

interface Responsibility {
  title: string;
  description: string;
}

interface Qualification {
  description: string;
}

interface JobData {
  jobTitle: string;
  organization: string;
  employmentType: string;
  work_mode?: string;
  salaryRange: string;
  displaySalary?: boolean;
  experience_level: string;
  experience_environment: string;
  salary_range_term: string;
  salary_range_currency: string;
  years_of_experience: string;
  responsibilities: Responsibility[];
  qualifications: Qualification[];
  // candidate-only fields
  location?: string;
  about?: string;
  whatWeOffer?: string[];
}

interface JobDescriptionProps {
  jdData?: JobData;
  onEdit?: (section: string) => void;
  assessmentId: string | null;
  hideHeader?: boolean;
}

export function parseJdPreview(jd: string | null | undefined): JobData {
  let parsedData: any = {};

  if (jd) {
    try {
      parsedData = JSON.parse(jd);
    } catch (error) {
      console.error("Invalid JSON format in jd_preview:", error);
    }
  }

  const jobData: JobData = {
    jobTitle: parsedData.jobTitle || "Untitled Job",
    organization: parsedData.organization || "Not specified",
    employmentType: parsedData.employmentType || "Not specified",
    work_mode: normalizeWorkMode(parsedData.work_mode || parsedData.workMode),
    salaryRange: parsedData.salaryRange || "Not specified",
    displaySalary: parsedData.displaySalary ?? false,
    experience_level: parsedData?.experience_level || "Not specified",
    experience_environment: parsedData?.experience_environment || "Not specified",
    salary_range_term: parsedData?.salary_range_term || "Not specified",
    salary_range_currency: parsedData?.salary_range_currency || "Not specified",
    years_of_experience: parsedData?.years_of_experience || "Not specified",
    location: parsedData.location || parsedData.address,
    responsibilities: Array.isArray(parsedData?.responsibilities)
      ? parsedData.responsibilities.map((r: any) => ({
        title: r?.title || "Untitled Responsibility",
        description: r?.description || "No description provided",
      }))
      : [],
    qualifications: Array.isArray(parsedData?.qualifications)
      ? parsedData.qualifications.map((q: any) => ({
        description: q?.description || "No description provided",
      }))
      : [],
  };

  return jobData;
}

const CANDIDATE_URL_PATTERNS = ['/candidate-assessment/', '/take-assessment/'];

const JobDescription: React.FC<JobDescriptionProps> = ({ jdData, onEdit, assessmentId, hideHeader }) => {
  const isCandidatePage = CANDIDATE_URL_PATTERNS.some(p => window.location.pathname.includes(p));
  const shouldHideHeader = isCandidatePage ? false : hideHeader;
  const [localJdData, setLocalJdData] = useState<JobData | undefined>(jdData);
  const [isBasicExpectationModalOpen, setIsBasicExpectationModalOpen] = useState(false);
  const [isResponsibilitiesModalOpen, setIsResponsibilitiesModalOpen] = useState(false);
  const [isQualificationsModalOpen, setIsQualificationsModalOpen] = useState(false);

  // Fetch whenever we have an assessmentId — the basic_expectation data (min/max
  // salary, requirement flags) lives there, not in jdData. React Query caches the
  // response so this is a single fetch even when jdData is also provided.
  const { assessmentInfo, fetchAssessmentInfo } = useGetAssessmentInfo(assessmentId);

  useEffect(() => {
    setLocalJdData(jdData);
  }, [jdData]);

  useEffect(() => {
    if (assessmentId) {
      fetchAssessmentInfo();
    }
  }, [assessmentId]);

  // Only initialize hooks when editing is enabled (employer side)
  const {
    submitBasicExpectations,
    isSubmitting: isSubmittingBasicExpectation,
  } = useBasicExpectationsEdit(onEdit ? assessmentId : null);

  const {
    submitCoreResponsibilities,
    isSubmitting: isSubmittingResponsibilities,
  } = useCoreResponsibilitiesEdit(onEdit ? assessmentId : null);

  const {
    submitJdQualifications,
    isSubmitting: isSubmittingQualifications,
  } = useJdQualificationsEdit(onEdit ? assessmentId : null);

  const handleEdit = useCallback((section: string) => {
    if (section === 'jobDetails') {
      setIsBasicExpectationModalOpen(true);
    } else if (section === 'responsibilities') {
      setIsResponsibilitiesModalOpen(true);
    } else if (section === 'qualifications') {
      setIsQualificationsModalOpen(true);
    }
  }, []);

  const basicExpectationCurrentData = useMemo<BasicExpectationFormData>(() => {
    const be = assessmentInfo?.data?.basic_expectation;
    return {
      employmentType: be?.employment_type || localJdData?.employmentType || '',
      isEmploymentType: be?.require_employment_type ?? true,
      experienceLevel: be?.experience_level || localJdData?.experience_level || '',
      isExperienceLevel: be?.require_experience_level ?? true,
      yearsOfExperience: be?.years_of_experience || localJdData?.years_of_experience || '',
      isYearsOfExperience: be?.require_years_of_experience ?? true,
      workMode: normalizeWorkMode(be?.work_mode || localJdData?.work_mode),
      jobLocation: be?.address || localJdData?.location || '',
      isWorkMode: be?.require_work_mode ?? true,
      workEnvironment: be?.experience_environment || localJdData?.experience_environment || '',
      isWorkEnvironment: be?.require_work_environment ?? true,
      minSalary: be?.min_salary_range_amount?.toString() ?? '',
      maxSalary: be?.max_salary_range_amount?.toString() ?? '',
      salaryCurrency: be?.salary_range_currency || localJdData?.salary_range_currency || '',
      salaryPeriod: be?.salary_range_term || localJdData?.salary_range_term || '',
      isSalaryRange: be?.require_salary_range ?? true,
      displaySalary: be?.display_salary ?? localJdData?.displaySalary ?? false,
      isCV: be?.require_cv_upload ?? true,
      isPortfolio: be?.require_portfolio_upload ?? true,
    };
  }, [assessmentInfo, localJdData]);

  const handleBasicExpectationSave = useCallback((formData: BasicExpectationFormData) => {
    if (!assessmentId) return;

    // Only send the fields the user actually changed (PATCH leaves the rest untouched).
    const payload = buildBasicExpectationPatch(formData, basicExpectationCurrentData, assessmentId);

    // Nothing changed — just close the modal, no redundant request.
    if (!hasBasicExpectationChanges(payload)) {
      setIsBasicExpectationModalOpen(false);
      return;
    }

    submitBasicExpectations(payload, {
      onSuccess: () => {
        setLocalJdData(prev => prev ? {
          ...prev,
          employmentType: formData.employmentType || prev.employmentType,
          experience_level: formData.experienceLevel || prev.experience_level,
          years_of_experience: formData.yearsOfExperience || prev.years_of_experience,
          work_mode: normalizeWorkMode(formData.workMode) || prev.work_mode,
          location: shouldCollectJobLocation(formData.workMode)
            ? formData.jobLocation
            : undefined,
          experience_environment: formData.workEnvironment || prev.experience_environment,
          salary_range_term: formData.salaryPeriod || prev.salary_range_term,
          salary_range_currency: (formData.salaryCurrency ?? '').toUpperCase() || prev.salary_range_currency,
          salaryRange: formData.minSalary && formData.maxSalary
            ? `${Number(formData.minSalary).toLocaleString()} - ${Number(formData.maxSalary).toLocaleString()}`
            : prev.salaryRange,
        } : prev);
        fetchAssessmentInfo();
        setIsBasicExpectationModalOpen(false);
      },
      onError: () => {
        setIsBasicExpectationModalOpen(false);
      },
    });
  }, [assessmentId, submitBasicExpectations, fetchAssessmentInfo, basicExpectationCurrentData]);

  const handleResponsibilitiesSave = useCallback((formData: { responsibilities: { title: string; description: string; checked: boolean }[] }) => {
    if (!assessmentId) return;

    const payload: CoreResponsibilitiesPayload = {
      assessment_id: assessmentId,
    };

    formData.responsibilities.forEach((r, index) => {
      const num = index + 1;
      payload[`responsibility${num}`] = r.title;
      payload[`description${num}`] = r.description;
    });

    submitCoreResponsibilities(payload, {
      onSuccess: () => {
        setLocalJdData(prev => prev ? {
          ...prev,
          responsibilities: formData.responsibilities.map(r => ({
            title: r.title,
            description: r.description,
          })),
        } : prev);
        setIsResponsibilitiesModalOpen(false);
      },
      onError: () => {
        setIsResponsibilitiesModalOpen(false);
      },
    });
  }, [assessmentId, submitCoreResponsibilities]);

  const handleQualificationsSave = useCallback((formData: { qualifications: { description: string }[] }) => {
    if (!assessmentId) return;

    const payload: JdQualificationsPayload = {
      assessment_id: assessmentId,
    };

    formData.qualifications.forEach((q, index) => {
      const num = index + 1;
      payload[`description${num}`] = q.description;
    });

    submitJdQualifications(payload, {
      onSuccess: () => {
        setLocalJdData(prev => prev ? {
          ...prev,
          qualifications: formData.qualifications.map(q => ({
            description: q.description,
          })),
        } : prev);
        setIsQualificationsModalOpen(false);
      },
      onError: () => {
        setIsQualificationsModalOpen(false);
      },
    });
  }, [assessmentId, submitJdQualifications]);

  const responsibilities = localJdData?.responsibilities ?? [];
  const qualifications = localJdData?.qualifications ?? [];

  const responsibilitiesCurrentData = {
    responsibilities: responsibilities.map(r => ({
      title: r.title,
      description: r.description,
      checked: true,
    })),
  };

  const qualificationsCurrentData = {
    qualifications: qualifications.map(q => ({
      description: q.description,
    })),
  };

  if (isCandidatePage) {
    return (
      <div className={styles.jobDescriptionContainer}>
        {!shouldHideHeader && (
          <div className={`mt-7 ${styles.sectionContainer}`}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Job Description</h2>
            </div>
          </div>
        )}

        <div className={styles.jobDetailsContainer}>
          <div className={styles.divider}></div>
          <h3 className={`pb-2 ${styles.jobTitle}`}>{localJdData?.jobTitle || "Untitled Job"}</h3>

          {localJdData?.organization && localJdData.organization !== 'Not specified' && (
            <div className={styles.detailsRow}>
              <span className={styles.detailLabel}>Representing Organization:</span>
              <span className={styles.detailValue}>{localJdData.organization}</span>
            </div>
          )}

          {localJdData?.work_mode && (
            <div className={styles.detailsRow}>
              <span className={styles.detailLabel}>Work mode:</span>
              <span className={styles.detailValue}>{formatWorkMode(localJdData.work_mode)}</span>
            </div>
          )}

          {shouldCollectJobLocation(localJdData?.work_mode) && localJdData?.location && localJdData.location !== 'Not specified' && (
            <div className={styles.detailsRow}>
              <span className={styles.detailLabel}>Job location:</span>
              <span className={styles.detailValue}>{localJdData.location}</span>
            </div>
          )}

          {localJdData?.employmentType && localJdData.employmentType !== 'Not specified' && (
            <div className={styles.detailsRow}>
              <span className={styles.detailLabel}>Employment type:</span>
              <span className={styles.detailValue}>{toTitleCase(localJdData.employmentType)}</span>
            </div>
          )}

          {localJdData?.salaryRange && localJdData.salaryRange !== 'Not specified' && (
            <div className={styles.detailsRow}>
              <span className={styles.detailLabel}>Salary range:</span>
              <span className={styles.detailValue}>
                {localJdData.salaryRange} 
              </span>
            </div>
          )}

          <div className={styles.divider}></div>
        </div>

        {localJdData?.about && (
          <div className={styles.sectionContainer}>
            <div className={`mb-4 ${styles.sectionHeader}`}>
              <h3 className={styles.sectionTitle}>About</h3>
            </div>
            <p className={styles.aboutText}>{localJdData.about}</p>
          </div>
        )}

        {responsibilities.length > 0 && (
          <div className={styles.sectionContainer}>
            <div className={`mb-3 ${styles.sectionHeader}`}>
              <h3 className={styles.sectionTitle}>Key Responsibilities</h3>
            </div>
            <ul className={styles.listContainer}>
              {responsibilities.map((responsibility, index) => (
                <li key={index} className={`${styles.listItem} !mb-1`}>
                  <span className={styles.responsibilityTitle}>{responsibility?.title}</span>
                  {' — '}
                  <span className={styles.responsibilityDescription}>{responsibility?.description}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {qualifications.length > 0 && (
          <div className={styles.sectionContainer}>
            <div className={`mb-3 ${styles.sectionHeader}`}>
              <h3 className={styles.sectionTitle}>Qualification</h3>
            </div>
            <ul className={styles.listContainer}>
              {qualifications.map((qualification, index) => (
                <li key={index} className={`${styles.listItem} !mb-1`}>
                  {qualification?.description}
                </li>
              ))}
            </ul>
          </div>
        )}

        {localJdData?.whatWeOffer && localJdData.whatWeOffer.length > 0 && (
          <div className={styles.sectionContainer}>
            <div className={`mb-3 ${styles.sectionHeader}`}>
              <h3 className={styles.sectionTitle}>What We Offer</h3>
            </div>
            <ul className={styles.listContainer}>
              {localJdData.whatWeOffer.map((item, index) => (
                <li key={index} className={styles.listItem}>{item}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={styles.jobDescriptionContainer}>
      {!shouldHideHeader && (
        <div className={`mt-7 ${styles.sectionContainer}`}>
          <div className={styles.sectionHeader}>
            <h2 className={styles.sectionTitle}>Job Description</h2>
          </div>
        </div>
      )}

      <div className={styles.jobDetailsContainer}>
        <div className={styles.divider}></div>
        <div className={`mb-3 ${styles.titleContainer}`}>
          <h3 className={styles.jobTitle}>Basic Expectation</h3>
          {onEdit && (
            <button className={styles.editButton} onClick={() => handleEdit('jobDetails')}>
              <EditIcon3 />
            </button>
          )}
        </div>

        <div className={styles.detailsRow}>
          <span className={styles.detailLabel}>Employment type:</span>
          <span className={styles.detailValue}>{localJdData?.employmentType ? toTitleCase(localJdData.employmentType) : "Not specified"}</span>
        </div>

        <div className={styles.detailsRow}>
          <span className={styles.detailLabel}>Experience level:</span>
          <span className={styles.detailValue}>{localJdData?.experience_level ? toTitleCase(localJdData.experience_level) : "Not specified"}</span>
        </div>

        <div className={styles.detailsRow}>
          <span className={styles.detailLabel}>Years of experience:</span>
          <span className={styles.detailValue}>{Number(localJdData?.years_of_experience) !== 0 && localJdData?.years_of_experience ? formatYearsRange(localJdData?.years_of_experience) : "Not specified"}</span>
        </div>

        {/* <div className={styles.detailsRow}>
          <span className={styles.detailLabel}>Work mode:</span>
          <span className={styles.detailValue}>{formatWorkMode(localJdData?.work_mode) || "Not specified"}</span>
        </div> */}

        {shouldCollectJobLocation(localJdData?.work_mode) && (
          <div className={styles.detailsRow}>
            <span className={styles.detailLabel}>Job location:</span>
            <span className={styles.detailValue}>{localJdData?.location || "Not specified"}</span>
          </div>
        )}

        <div className={styles.detailsRow}>
          <span className={styles.detailLabel}>Work environment preference:</span>
          <span className={styles.detailValue}>{localJdData?.experience_environment ? toTitleCase(localJdData.experience_environment) : "Not specified"}</span>
        </div>

        <div className={styles.detailsRow}>
          <span className={styles.detailLabel}>Salary range:</span>
          <span className={styles.detailValue}>
            {localJdData?.salaryRange && localJdData.salaryRange !== "Not specified"
              ? `${localJdData.salaryRange}  ${localJdData.salary_range_currency || ""} (${localJdData.salary_range_term || ""})`
              : "Not specified"}
          </span>
        </div>

        <div className={styles.divider}></div>
      </div>

      <div className={styles.sectionContainer}>
        <div className={`mb-3 ${styles.sectionHeader}`}>
          <h3 className={styles.sectionTitle}>Key Responsibilities</h3>
          {onEdit && (
            <button className={styles.editButton} onClick={() => handleEdit('responsibilities')}>
              <EditIcon3 />
            </button>
          )}
        </div>

        {responsibilities.length > 0 ? (
          <ul className={styles.listContainer}>
            {responsibilities.map((responsibility, index) => (
              <li key={index} className={styles.listItem}>
                <span className={styles.responsibilityTitle}>
                  {responsibility?.title || "Untitled Responsibility"}
                </span>
                {' — '}
                <span className={styles.responsibilityDescription}>
                  {responsibility?.description || "No description provided"}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.emptyState}>No responsibilities provided.</p>
        )}

        <div className={styles.divider}></div>
      </div>

      <div className={styles.sectionContainer}>
        <div className={styles.sectionHeader}>
          <h3 className={styles.sectionTitle}>Qualification</h3>
          {onEdit && (
            <button className={styles.editButton} onClick={() => handleEdit('qualifications')}>
              <EditIcon3 />
            </button>
          )}
        </div>
        <div className={`${styles.divider}`}></div>

        {qualifications.length > 0 ? (
          <ul className={styles.listContainer}>
            {qualifications.map((qualification, index) => (
              <li key={index} className={styles.listItem}>
                {qualification?.description || "No description provided"}
              </li>
            ))}
          </ul>
        ) : (
          <p className={styles.emptyState}>No qualifications provided.</p>
        )}

        <div className={styles.divider}></div>
      </div>

      {onEdit && (
        <p className={styles.editMessage}>
          You can adjust or remove any of these using the editing and delete button respectively
        </p>
      )}

      {onEdit && (
        <>
          <BasicExpectationEditModal
            isOpen={isBasicExpectationModalOpen}
            onClose={() => setIsBasicExpectationModalOpen(false)}
            currentData={basicExpectationCurrentData}
            onSave={handleBasicExpectationSave}
            isSubmitting={isSubmittingBasicExpectation}
            showToggles={false}
          />

          <CoreResponsibilitiesEditModal
            isOpen={isResponsibilitiesModalOpen}
            onClose={() => setIsResponsibilitiesModalOpen(false)}
            currentData={responsibilitiesCurrentData}
            onSave={handleResponsibilitiesSave}
            isSubmitting={isSubmittingResponsibilities}
            title={'Job Description'}
          />

          <JdQualificationsEditModal
            isOpen={isQualificationsModalOpen}
            onClose={() => setIsQualificationsModalOpen(false)}
            currentData={qualificationsCurrentData}
            onSave={handleQualificationsSave}
            isSubmitting={isSubmittingQualifications}
            title={'Job Description'}
          />
        </>
      )}
    </div>
  );
};

export { JobDescription };
